import React, { useState } from "react";
import axios from "axios";
import { Speaker } from "lucide-react";
import { Link } from "react-router-dom";

const languages = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "bn", label: "Bengali" },
  { code: "ar", label: "Arabic" },
  { code: "he", label: "Hebrew" },
  { code: "hi", label: "Hindi" },
  { code: "ru", label: "Russian" },
];

const FlashcardForm = ({ token, onFlashcardSaved, fetchFlashcards }) => {

  const [form, setForm] = useState({
    originalText: "",
    sourceLang: "en",
    targetLang: "es",
  });

  const [translatedText, setTranslatedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Helper to get language label from code
  const findLanguageLabel = (code) => {
    const lang = languages.find((l) => l.code === code);
    return lang ? lang.label : code;
  };

  //text to speech
  const playWithBrowserTTS = (text, langCode) => {
    if (!window.speechSynthesis) {
      alert("Your browser does not support Text-to-Speech.");
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = langCode;
    utterance.rate = 1;
    utterance.pitch = 1;

    window.speechSynthesis.speak(utterance);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Allow editing translatedText textarea separately
  const handleTranslatedChange = (e) => {
    setTranslatedText(e.target.value);
  };

  const handleTranslate = async () => {
    if (!form.originalText.trim()) return setMessage("Please enter some text");
    setMessage("");
    setIsLoading(true);
    setShowLoginPrompt(false);

    try {
      const res = await axios.post(
        "https://langbridge-backend.onrender.com/api/translate",
        {
          text: form.originalText,
          sourceLang: form.sourceLang,
          targetLang: form.targetLang,
        },
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : {}
      );
      setTranslatedText(res.data.translatedText || "");
    } catch (error) {
      setMessage("Translation failed. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!token) {
      setShowLoginPrompt(true);
      return;
    }
    setMessage("");
    setShowLoginPrompt(false);

    try {
      await axios.post(
        "https://langbridge-backend.onrender.com/api/flashcards",
        {
          originalText: form.originalText,
          translatedText,
          sourceLang: form.sourceLang,
          targetLang: form.targetLang,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMessage("Flashcard saved!");
      setForm({ ...form, originalText: "" });
      setTranslatedText("");
      if (onFlashcardSaved) onFlashcardSaved();
      if (fetchFlashcards) fetchFlashcards();
    } catch (err) {
      setMessage("Failed to save flashcard.");
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl p-6 w-full max-w-3xl mx-auto pt-6">
      <h2 className="text-2xl font-bold mb-6 text-center text-blue">
        Create Flashcard
      </h2>

      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="flex-1">
          <label className="block text-sm font-medium mb-1 text-purple">
            From (source language)
          </label>
          <select
            name="sourceLang"
            value={form.sourceLang}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium mb-1 text-purple">
            To (target language)
          </label>
          <select
            name="targetLang"
            value={form.targetLang}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2"
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Original Textarea with Speaker Icon */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1 text-purple">
          Enter your text
        </label>
        <div className="relative">
          <textarea
            name="originalText"
            value={form.originalText}
            onChange={handleChange}
            rows={3}
            placeholder="Enter text to translate..."
            className="w-full border border-gray-300 rounded px-3 py-2 pr-10 resize-none"
          />
          {form.originalText.trim() && (
            <button
              type="button"
              onClick={() => playWithBrowserTTS(form.originalText, form.sourceLang)}
              aria-label={`Play original text in ${findLanguageLabel(
                form.sourceLang
              )}`}
              title={`Play original text (${findLanguageLabel(
                form.sourceLang
              )})`}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 text-blue hover:text-purple"
            >
              <Speaker className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {/* Translated Textarea with Speaker Icon */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1 text-purple">
          Translated text
        </label>
        <div className="relative">
          <textarea
            name="translatedText"
            value={translatedText}
            onChange={handleTranslatedChange}
            rows={3}
            placeholder="Translated text will appear here..."
            className="w-full border border-gray-300 rounded px-3 py-2 pr-10 resize-none"
          />
          {translatedText.trim() && (
            <button
              type="button"
              onClick={() => playWithBrowserTTS(translatedText, form.targetLang)}
              aria-label={`Play translated text in ${findLanguageLabel(
                form.targetLang
              )}`}
              title={`Play translated text (${findLanguageLabel(
                form.targetLang
              )})`}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 text-blue hover:text-purple"
            >
              <Speaker className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <button
          onClick={handleTranslate}
          className="bg-blue text-gray py-2 px-4 rounded hover:bg-purple w-full sm:w-auto"
          disabled={isLoading}
        >
          {isLoading ? "Translating..." : "Translate"}
        </button>

        {translatedText && (
          <button
            onClick={handleSave}
            className="bg-purple text-white py-2 px-4 rounded hover:bg-blue w-full sm:w-auto"
          >
            Save Flashcard
          </button>
        )}
      </div>

      {message && <p className="text-red-600 text-sm mt-2">{message}</p>}

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm text-center">
            <h2 className="text-lg font-semibold mb-2 text-purple">Login Required</h2>
            <p className="mb-4 text-sm text-gray-700">
              You must be logged in to save flashcards.
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="/login"
                className="bg-blue text-white px-4 py-2 rounded hover:bg-purple transition"
              >
                Go to Login
              </Link>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlashcardForm;
